import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as languagesApi from './languages.api';

import type {
  CreateLanguageRequest,
  LanguageListQuery,
  UpdateLanguageRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: LanguageListQuery) => ['languages', 'list', query] as const,
  detail: (id: string) => ['languages', 'detail', id] as const,
};

export const useLanguagesList = (query: LanguageListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => languagesApi.listLanguages(query),
    staleTime: 30_000,
  });

export const useCreateLanguage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateLanguageRequest) => languagesApi.createLanguage(payload),
    onSuccess: (l) => {
      qc.invalidateQueries({ queryKey: ['languages', 'list'] });
      toast.success(`Added ${l.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add language'),
  });
};

export const useUpdateLanguage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateLanguageRequest }) =>
      languagesApi.updateLanguage(id, payload),
    onSuccess: (l) => {
      qc.invalidateQueries({ queryKey: ['languages', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(l.id) });
      toast.success('Language updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update language'),
  });
};

export const useDeleteLanguage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => languagesApi.deleteLanguage(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['languages', 'list'] });
      toast.success('Language deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete language'),
  });
};

export const useBulkDeleteLanguages = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => languagesApi.bulkDeleteLanguages(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['languages'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete languages'),
  });
};

export const useBulkSetLanguagesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      languagesApi.bulkSetLanguagesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['languages'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update language status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
